/**
 * Feedback Triage — API service.
 *
 * Reads the open reports back out of #people_spaces_feedback and writes an owner, a status, a
 * decision and a fix link onto them. The proxy authorizes
 * `space.people.internal_playground.view` (always enforced) on every call — see
 * `proxy-server/routes/feedback-triage.js`.
 *
 * What is NOT here, on purpose: any notion of a closed report. The proxy never sends one. A
 * report carrying :white_check_mark: in Slack is done and simply stops being in the list, so
 * there is no "show closed" toggle to get out of sync with the channel.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** `done` and `noise` close a report — both put the checkmark on the Slack post. */
export type TriageStatus = 'new' | 'triaged' | 'progress' | 'waiting' | 'done' | 'noise';

/** A closed list, not a directory lookup — see `TRIAGE_OWNERS` in the proxy, which is the one
 * place it is defined and the one place it is enforced. */
export type OwnerId = 'theresa' | 'anna' | 'sofia' | 'david';

export interface TriageOwner {
  id: OwnerId;
  name: string;
  email: string;
  initials: string;
}

export interface ReplyResult {
  ok: true;
  repliedAt: string;
  repliedBy: string;
}

export type FeedbackType = 'bug' | 'feature' | 'improvement' | 'other';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Where a row's owner came from. Every report has one (Oct 9 2026): a person's assignment, the
 * owner Pia addressed, or the page's default from `proxy-server/lib/feedbackOwnership.js`. The
 * board treats all three the same (Sofia, Oct 19 2026): an automatic owner IS the owner, and
 * whoever does not want a report hands it over on the board — there is no confirm step.
 */
export type OwnerSource = 'assigned' | 'pia' | 'default';

export type PiaPhase =
  | 'running'
  | 'waiting'
  | 'pr-open'
  | 'merge-pending'
  | 'merged'
  | 'merge-blocked'
  | 'pr-closed'
  | 'needs-discussion'
  | 'not-a-bug'
  | 'failed'
  | 'capped'
  | 'stopped'
  | 'dispatch-failed'
  | 'idle';

/** Pia's run on this report, joined by the proxy at read time (`proxy-server/lib/pia/boardSummary.js`). */
export interface PiaSummary {
  phase: PiaPhase;
  label: string;
  prUrl: string;
  prNumber: number;
  riskLevel: string;
  rounds: number;
  ownerId: OwnerId | null;
  at: string | null;
}

export interface FeedbackReport {
  /** The Slack message timestamp — the report's id everywhere, here and in Firestore. */
  ts: string;
  title: string;
  description: string;
  /** EFFECTIVE type and priority: an owner's correction on the board, else what was filed. */
  type: FeedbackType;
  priority: FeedbackPriority;
  /** What the reporter filed, kept so the board can say "filed as critical". */
  reportedType: FeedbackType;
  reportedPriority: FeedbackPriority;
  /** The page as the SIDEBAR names it, and its sub-tab: "TA Action Boards", "Scheduling". */
  surface: string;
  subTab: string | null;
  area: string;
  reporter: string;
  pagePath: string;
  screenshots: number;
  /** True when the report quotes a candidate's message — the quote itself stays in the thread. */
  hasCandidateContext: boolean;
  replies: number;
  postedAt: string;
  /** The exact post, not the channel: .../archives/<channel>/p<ts>. Empty if Slack was unreachable. */
  permalink: string;
  reactions: string[];
  status: TriageStatus;
  /** Never null — see `OwnerSource`. */
  owner: OwnerId;
  ownerSource: OwnerSource;
  decision: string;
  fixUrl: string;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Set once a closing note has been posted into this report's thread. */
  repliedAt: string | null;
  repliedBy: string | null;
  /** Null when Pia never handled this report. */
  pia: PiaSummary | null;
}

export interface ReportsResponse {
  reports: FeedbackReport[];
  owners: TriageOwner[];
  syncedAt: string;
  cached: boolean;
  channelUrl: string;
}

/**
 * What the page can change about a report. `owner: null` hands it back to the page's default
 * owner; `priority: null` / `type: null` go back to what the reporter filed.
 */
export interface TriagePatch {
  status?: TriageStatus;
  owner?: OwnerId | null;
  priority?: FeedbackPriority | null;
  type?: FeedbackType | null;
  decision?: string;
  fixUrl?: string;
}

export interface PatchResponse {
  ts: string;
  status: TriageStatus;
  /** The STORED owner — null when nobody assigned one. The row's effective owner is on `report`. */
  owner: OwnerId | null;
  decision?: string;
  fixUrl?: string;
  updatedAt: string | null;
  updatedBy: string | null;
  /**
   * The whole row as the list would now show it (owner, effective priority/type, Pia), or null
   * when the report is no longer open — the page then re-reads. Carries no `permalink`.
   */
  report: Omit<FeedbackReport, 'permalink'> | null;
  /**
   * Things Slack would not do, in words a person can act on — a missing scope, or a checkmark a
   * human put on that the bot cannot take off. The status is saved either way; these say what the
   * CHANNEL does not yet reflect, which is exactly the part a silent failure would hide.
   */
  warnings: string[];
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchReports(force = false): Promise<ReportsResponse> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/feedback-triage/reports${force ? '?force=1' : ''}`
  );
  if (!res.ok) throw new Error(await readError(res, 'Could not load the feedback reports.'));
  return res.json();
}

export async function patchReport(ts: string, patch: TriagePatch): Promise<PatchResponse> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/feedback-triage/reports/${ts}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not save that change.'));
  return res.json();
}

/**
 * Post the closing note into the report's own thread.
 *
 * The thread is the `ts` in the URL, resolved server-side; the body carries the sentence only.
 * A 409 means somebody already replied to this report — the proxy allows exactly one, because
 * Slack does not deduplicate a repeated message the way it deduplicates a repeated reaction.
 */
export async function postThreadReply(ts: string, text: string): Promise<ReplyResult> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/feedback-triage/reports/${ts}/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not post the note to Slack.'));
  return res.json();
}

/** One of Pia's standing instructions — a "going forward, …" rule an owner gave her in a thread. */
export interface PiaLesson {
  id: string;
  text: string;
  by: string;
  threadTs: string;
  createdAt: string;
}

export async function fetchPiaLessons(): Promise<{ lessons: PiaLesson[] }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/feedback-triage/pia-lessons`);
  if (!res.ok) throw new Error(await readError(res, 'Could not load Pia’s standing instructions.'));
  return res.json();
}

export async function deletePiaLesson(id: string): Promise<void> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/feedback-triage/pia-lessons/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not remove that instruction.'));
}
